import { createStore, SetState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { isOrgProfile, OrgProfile } from '~/modules/appx_site_schema';
import { IS_DEVELOPMENT } from '~/modules/config';
import { OrgInfo } from '~/modules/services/userService';
import { OrgProfileService } from '~/modules/services/orgProfileService';
import { CookieConsentCategory, createCatCookie, createCookie, getCookieConsented } from '~/modules/utilities/cookies';
import { domain } from '~/modules/utilities/url_utilities';

const SELECTED_ORG_COOKIE_NAME = 'agentx_selected_org';
const SELECTED_ORG_COOKIE_EXPIRY_DAYS = 7;
const ORG_PROFILE_STORAGE_KEY = 'selectedOrgProfile';

const orgProfileService = new OrgProfileService();

// Dedupes concurrent loadOrgProfile calls for the SAME org: while a fetch is in flight, later callers
// (homepage personalization, orgContextMenu, etc.) get the same promise instead of racing a second
// fetch whose later resolution could clobber the first's state and sessionStorage write. Keyed on
// orgId so a concurrent load for a different org can't return the wrong org's profile.
let inFlightProfileLoad: { orgId: string; promise: Promise<OrgProfile | null> } | null = null;

/** Clears the in-flight load guard. @internal Exported for test isolation only. */
export function resetInFlightProfileLoad(): void {
    inFlightProfileLoad = null;
}

// Only the fields the header / org-context menu render are persisted. tenantKey and instanceId are
// internal ids the UI never reads, kept out of the cookie to minimize PII at rest. Exported so
// orgContextHelper's staleness check compares only fields a rehydrated selection can actually carry.
export const PERSISTED_ORG_FIELDS = ['orgId', 'name', 'username', 'domain', 'edition'] as const;

function clearSelectedOrgCookie(): void {
    createCookie(SELECTED_ORG_COOKIE_NAME, '', -1);
    const cookieDomain = domain(window.location.hostname);
    if (cookieDomain) {
        createCookie(SELECTED_ORG_COOKIE_NAME, '', -1, undefined, cookieDomain);
    }
}

// Functional-category cookie (same gate as recentItemService/userTokenService): a user who has
// opted out of Functional cookies simply re-selects their org each visit rather than persisting.
function persistSelectedOrg(org: OrgInfo | null): void {
    if (org) {
        const trimmed: Pick<OrgInfo, (typeof PERSISTED_ORG_FIELDS)[number]> = {
            orgId: org.orgId,
            name: org.name,
            username: org.username,
            domain: org.domain,
            edition: org.edition,
        };
        createCatCookie(
            SELECTED_ORG_COOKIE_NAME,
            encodeURIComponent(JSON.stringify(trimmed)),
            CookieConsentCategory.Functional,
            SELECTED_ORG_COOKIE_EXPIRY_DAYS,
            undefined,
            domain(window.location.hostname),
        );
    } else {
        // Deletion always runs, even if Functional consent has since been revoked — otherwise a
        // revoked-then-re-granted consent cycle would resurrect a selection the user asked to clear.
        clearSelectedOrgCookie();
    }
}

function persistOrgProfile(profile: OrgProfile | null): void {
    try {
        if (profile) {
            window.sessionStorage.setItem(ORG_PROFILE_STORAGE_KEY, JSON.stringify(profile));
        } else {
            window.sessionStorage.removeItem(ORG_PROFILE_STORAGE_KEY);
        }
    } catch {
        /* ignore quota/security errors */
    }
}

// A rehydrated selection is trusted only if every persisted field is a string; a wrong/old shape is
// treated as corrupt rather than blind-cast.
function isOrgInfo(value: unknown): value is OrgInfo {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Record<string, unknown>;
    return PERSISTED_ORG_FIELDS.every((field) => typeof candidate[field] === 'string');
}

/**
 * Reads the persisted selection. Corrupt/wrong-shape storage is cleared and treated as "no selection"
 * so it can't re-fail on every load — per W-22901453 ("corrupt stored context is cleared … never throws").
 * @internal Exported for unit tests.
 */
export function loadSelectedOrg(): OrgInfo | null {
    const raw = getCookieConsented(SELECTED_ORG_COOKIE_NAME, CookieConsentCategory.Functional);
    if (!raw) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(decodeURIComponent(raw));
        if (isOrgInfo(parsed)) {
            return parsed;
        }
    } catch {
        /* fall through to clearing the corrupt cookie below */
    }
    clearSelectedOrgCookie();
    return null;
}

/**
 * Reads the persisted org profile; corrupt/wrong-shape storage is cleared and treated as none
 * (re-derived from OAF on the next selection).
 * @internal Exported for unit tests.
 */
export function loadOrgProfile(): OrgProfile | null {
    try {
        const raw = window.sessionStorage.getItem(ORG_PROFILE_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        if (isOrgProfile(parsed)) {
            return parsed;
        }
        window.sessionStorage.removeItem(ORG_PROFILE_STORAGE_KEY);
        return null;
    } catch {
        try {
            window.sessionStorage.removeItem(ORG_PROFILE_STORAGE_KEY);
        } catch {
            /* ignore security errors */
        }
        return null;
    }
}

/**
 * Source of truth for the selected org and its OAF profile. The /select-org page writes on
 * selection; the header org-context menu reads it. Both sit behind the shared org-picker gate
 * (FF_ORG_PICKER or pilot enrollment), so the store is inert until written to. The selected org is
 * persisted to a 7-day cookie (W-22903169) so it survives both the full-page navigation from
 * /select-org and a full browser close/reopen; the derived profile stays in sessionStorage
 * (session-only — cheap to re-fetch, no need to persist across visits). A null profile means the
 * fetch degraded — no homepage personalization.
 */
export interface OrgContextState extends State {
    selectedOrg: OrgInfo | null;
    orgProfile: OrgProfile | null;
}

const initialState: OrgContextState = {
    selectedOrg: loadSelectedOrg(),
    orgProfile: loadOrgProfile(),
};

export const orgContextStore = createStore(
    initialState,

    (set: SetState<OrgContextState>) => ({
        setSelectedOrg(org: OrgInfo) {
            persistSelectedOrg(org);
            set(function selectOrg(draft: OrgContextState) {
                draft.selectedOrg = org;
                return draft;
            });
        },

        clearSelectedOrg() {
            persistSelectedOrg(null);
            persistOrgProfile(null);
            set(function clear(draft: OrgContextState) {
                draft.selectedOrg = null;
                draft.orgProfile = null;
                return draft;
            });
        },

        // Fetches the org's profile via the proxy and session-caches it; returns it for the caller to
        // branch on. Never throws — the service degrades to null, stored as "no profile". Concurrent
        // calls share one in-flight fetch so a later caller can't clobber the first's write.
        loadOrgProfile(orgId: string): Promise<OrgProfile | null> {
            // Reuse the in-flight fetch only when it's for the same org, so a concurrent load for a
            // different org never resolves into the wrong org's profile.
            if (inFlightProfileLoad && inFlightProfileLoad.orgId === orgId) {
                return inFlightProfileLoad.promise;
            }
            const promise = orgProfileService
                .getOrgProfile(orgId)
                .then((profile) => {
                    // Only commit if this orgId is still the most-recently-requested one; a later
                    // selection for a different org may have started a new fetch and we must not
                    // clobber that org's profile with a stale resolution.
                    if (inFlightProfileLoad?.orgId !== orgId) {
                        return profile;
                    }
                    persistOrgProfile(profile);
                    set(function setOrgProfile(draft: OrgContextState) {
                        draft.orgProfile = profile;
                        return draft;
                    });
                    return profile;
                })
                .finally(() => {
                    // Only clear if this is still the tracked load (a newer different-org load may have
                    // replaced it).
                    if (inFlightProfileLoad?.promise === promise) {
                        inFlightProfileLoad = null;
                    }
                });
            inFlightProfileLoad = { orgId, promise };
            return promise;
        },
    }),
    'orgContextStore',
    IS_DEVELOPMENT,
);

export function selectSelectedOrg(state: OrgContextState): OrgInfo | null {
    return state.selectedOrg;
}

export function selectOrgProfile(state: OrgContextState): OrgProfile | null {
    return state.orgProfile;
}
